import { Box, Column, Columns, Stack, Text, Toggle } from '@machobear/design-system'
import { Trans, useTranslation } from 'react-i18next'
import { useState } from 'react'
import { isEmpty } from 'lodash'
import { useFormikContext } from 'formik'
import { Link } from 'react-router-dom'
import { Dropdown, FieldInput } from '@/components/ds-form'
import { ShieldIcon } from '@/components/icons'
import { PROVINCES } from '@/utils/address'
import usePayments from '@/hooks/use-payments'
import SavedPayments from './saved-payment'
import { FormBox, FormBoxSkeleton } from '../../components'

interface Props {
  enableValidation: () => void
}

const SummaryPayment = ({ enableValidation }: Props) => {
  const { t } = useTranslation()
  const { values, setFieldValue, setValues } = useFormikContext<any>()
  const [showEditForm, setShowEditForm] = useState(false)
  const paymentHook = usePayments()

  if (paymentHook.loading) {
    return <FormBoxSkeleton />
  }

  const PaymentInfoForm = (
    <>
      <Stack space="small">
        <Box>
          <Text weight="medium">{t('Credit card details')}</Text>
        </Box>
        <Columns space="xsmall" collapseBelow="desktop">
          <Column>
            <FieldInput
              name="first_name"
              label={t('b.checkout.field.first_name', 'First name')}
              required
            />
          </Column>
          <Column>
            <FieldInput
              name="last_name"
              label={t('b.checkout.field.last_name', 'Last name')}
              required
            />
          </Column>
        </Columns>
        <Columns space="xsmall" collapseBelow="desktop">
          <Column>
            <FieldInput
              name="card_number"
              maxLength={19}
              mask={(value: string) =>
                (value ?? '').replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ')
              }
              label={t('b.checkout.field.card_number', 'Card number')}
              required
            />
          </Column>
          <Column>
            <Columns space="xsmall">
              <Column>
                <FieldInput
                  name="expiration_date"
                  mask={(value: string) =>
                    (value ?? '').replace(/\D/g, '').replace(/(\d{2})(?=\d)/g, '$1/')
                  }
                  label={t('b.checkout.field.expiry', 'MM/YY')}
                  maxLength={5}
                  required
                />
              </Column>
              <Column>
                <FieldInput
                  name="security_code"
                  label={t('b.checkout.field.cvc', 'CVC')}
                  maxLength={4}
                  required
                />
              </Column>
            </Columns>
          </Column>
        </Columns>
      </Stack>
      <Stack space="small">
        <Box>
          <Text weight="medium">{t('Billing address')}</Text>
        </Box>
        <Columns space="xsmall" collapseBelow="desktop">
          <Column>
            <FieldInput
              name="street1"
              label={t('b.checkout.field.address', 'Street address')}
              required
            />
          </Column>
          <Column>
            <FieldInput
              name="street2"
              label={t('b.checkout.field.address_2', 'Suite no./ Unit no.')}
            />
          </Column>
        </Columns>
        <Columns space="xsmall" collapseBelow="desktop">
          <Column>
            <FieldInput name="city" label={t('b.checkout.field.city', 'City')} required />
          </Column>
          <Column>
            <Dropdown
              name="state"
              label={t('b.checkout.field.province', 'Province')}
              aria-labelledby="provinces"
              options={PROVINCES}
              required
            />
          </Column>
        </Columns>
        <Columns space="xsmall" collapseBelow="desktop">
          <Column>
            <Dropdown
              name="country"
              aria-labelledby="country"
              label={t('b.checkout.field.country', 'Country')}
              required
              options={[{ value: 'CA', label: 'Canada' }]}
            />
          </Column>
          <Column>
            <FieldInput name="zip" label={t('b.checkout.field.zip', 'Postal code')} required />
          </Column>
        </Columns>
        <Box marginY="small">
          <Toggle
            id="save_payment_method"
            label={t('b.checkout.field.save_payment_method', 'Save payment method')}
            on={values.save_payment_method === 'true'}
            onChange={val => {
              setFieldValue('save_payment_method', val ? 'true' : 'false')
            }}
          />
        </Box>
      </Stack>
    </>
  )

  return (
    <Box data-testid="PaymentMethod">
      <FormBox label="">
        <Box style={{ marginTop: '-40px' }}>
          {/* adjusting for the custom header */}
          <Stack space="large">
            <Box display="flex" justifyContent="spaceBetween" alignItems="center">
              <Text size="large" weight="strong">
                {t('b.checkout.title.payment_method', 'Payment Method')}
              </Text>
              <Box display="flex" alignItems="center" gap="xxxsmall">
                <ShieldIcon width={16} height={16} />
                <Text tone="tertiary" size="xsmall">
                  {t('b.checkout.title.secure_ecryption', 'Secure Encryption')}
                </Text>
              </Box>
            </Box>

            {isEmpty(paymentHook.paymentInfo) && PaymentInfoForm}

            {!isEmpty(paymentHook.paymentInfo) &&
              (showEditForm ? (
                PaymentInfoForm
              ) : (
                <SavedPayments
                  paymentInfo={paymentHook.paymentInfo ?? []}
                  onEdit={async (index: number) => {
                    setShowEditForm(true)
                    const {
                      first_name,
                      last_name,
                      card_number,
                      expiration_date,
                      security_code,
                      street1,
                      street2,
                      city,
                      state,
                      country,
                      zip
                    } = paymentHook?.paymentInfo[index] ?? {}
                    await setValues({
                      ...values,
                      first_name,
                      last_name,
                      card_number,
                      expiration_date,
                      security_code,
                      street1,
                      street2,
                      city,
                      state,
                      country,
                      zip,
                      update_payment: true
                    })
                    enableValidation()
                  }}
                />
              ))}
          </Stack>
        </Box>
        <Box style={{ marginTop: '-10px' }}>
          <Trans i18nKey="l.listing.edit.payment_summary.terms">
            <Text size="small" tone="tertiary">
              By clicking “Publish”, I agree to a one-time charge and confirm that I have read and
              agree to liv.rent's{' '}
              <Link to={`/privacy`} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </Link>{' '}
              &{' '}
              <Link to={`/terms`} target="_blank" rel="noopener noreferrer">
                Terms & Conditions
              </Link>
            </Text>
          </Trans>
        </Box>
      </FormBox>
    </Box>
  )
}

export default SummaryPayment
